/**
 * Detalhe e saúde de um monitorador.
 *
 * A pergunta que esta gaveta responde não é "qual o estado?" — isso a lista já
 * mostra. É "por que está assim, e o que eu faço?". Por isso cada linha vem
 * acompanhada do texto que explica, e `DEGRADED` ganha um bloco inteiro: é o
 * estado que parece bom (o número está conectado!) e custa caro, porque nada
 * está sendo capturado enquanto ele durar.
 *
 * Todo número aqui vem do servidor — inclusive a janela de silêncio
 * (`silenceThresholdMs`). Escrever "30 minutos" à mão criaria uma segunda
 * fonte da verdade que envelhece calada no dia em que a regra mudar.
 */
import {
  Badge,
  Button,
  Drawer,
  ErrorState,
  NO_DATA_DASH,
  Skeleton,
  UptimeStrip,
  StatusPill,
  useToast,
} from '@mgdlead/ui';
import type { MonitorStatus } from '@mgdlead/ui';
import { RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';

import { FormAlert } from '@mgdlead/ui';
import { MONITOR_HEALTH, MONITOR_STATUS_HINT } from '../../../features/monitor/monitor-ui';
import { useMonitorActivity, useMonitorHealth, useSyncMonitorStatus } from '../../../lib/api';
import type { Monitor } from '../../../lib/api';
import { formatDuration, formatExact, formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';

export interface MonitorDetailDrawerProps {
  open: boolean;
  workspaceId: string;
  timezone: string;
  monitor: Monitor | null;
  onOpenChange: (open: boolean) => void;
}

export function MonitorDetailDrawer({
  open,
  workspaceId,
  timezone,
  monitor,
  onOpenChange,
}: MonitorDetailDrawerProps) {
  const { toast } = useToast();
  const monitorId = monitor?.id ?? null;
  const healthQuery = useMonitorHealth(workspaceId, monitorId, open);
  const sync = useSyncMonitorStatus();

  const report = healthQuery.data;
  /* A sonda reconcilia com o provider; até ela voltar, vale o que a lista trouxe. */
  const status: MonitorStatus | null = report?.status ?? monitor?.status ?? null;
  const health = report?.health ?? monitor?.health ?? null;

  const handleSync = () => {
    if (monitorId === null) return;
    sync.mutate(
      { workspaceId, monitorId },
      {
        onSuccess: (result) =>
          toast({
            title: 'Estado sincronizado',
            description: `Agora consta como ${statusWord(result.status)}.`,
            tone: 'success',
          }),
        onError: (error: unknown) =>
          toast({
            title: 'Não deu para sincronizar',
            description: humanizeError(error),
            tone: 'danger',
          }),
      },
    );
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={monitor?.name ?? 'Monitorador'}
      description={monitor?.phoneE164 ?? 'O telefone aparece depois da primeira conexão.'}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button
            variant="secondary"
            leadingIcon={<RefreshCw />}
            loading={sync.isPending || healthQuery.isFetching}
            loadingLabel="Sincronizando o estado"
            onClick={handleSync}
          >
            Sincronizar estado
          </Button>
        </>
      }
    >
      {monitor === null ? null : (
        <div className="flex flex-col gap-4">
          {/* ----------------------------- estado ----------------------------- */}
          <section className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {status !== null ? <StatusPill status={status} /> : null}
              {health !== null ? (
                <Badge tone={MONITOR_HEALTH[health].tone} dot>
                  {MONITOR_HEALTH[health].label}
                </Badge>
              ) : null}
            </div>
            {status !== null ? (
              <p className="text-sm text-muted-foreground">{MONITOR_STATUS_HINT[status]}</p>
            ) : null}
          </section>

          {/* --------------------------- DEGRADED ----------------------------- */}
          {status === 'DEGRADED' ? <DegradedExplanation report={report} /> : null}

          {/* -------------------------- atividade ----------------------------- */}
          <Atividade workspaceId={workspaceId} monitorId={monitor.id} aberto={open} />

          {/* ---------------------------- fatos ------------------------------- */}
          <dl className="flex flex-col divide-y divide-border border-y border-border">
            <Fact
              label="Última conexão"
              value={relative(monitor.lastConnectedAt)}
              title={formatExact(monitor.lastConnectedAt, timezone)}
              empty="Nunca conectou"
            />
            <Fact
              label="Último evento recebido"
              value={relative(report?.lastEventAt ?? monitor.lastEventAt)}
              title={formatExact(report?.lastEventAt ?? monitor.lastEventAt, timezone)}
              empty="Nenhum evento até agora"
            />
            <Fact
              label="Grupos monitorados"
              value={monitor.groupsCount > 0 ? String(monitor.groupsCount) : '0'}
            />
            <Fact label="Serviço de conexão" value={monitor.provider} />
            {healthQuery.isSuccess && report !== undefined ? (
              <Fact
                label="Serviço no ar"
                value={
                  report.providerHealthy
                    ? `Sim${report.providerVersion !== null ? ` (versão ${report.providerVersion})` : ''}`
                    : 'Não — o serviço que conversa com o WhatsApp não respondeu'
                }
              />
            ) : null}
          </dl>

          {/* ---------------------------- erro -------------------------------- */}
          {(report?.lastError ?? monitor.lastError) !== null ? (
            <section className="flex flex-col gap-2">
              <p className="text-sm font-medium">Último erro</p>
              <FormAlert>{report?.lastError ?? monitor.lastError}</FormAlert>
            </section>
          ) : null}

          {healthQuery.isPending ? (
            <Skeleton className="h-4 w-48" aria-label="Consultando a saúde do monitorador" />
          ) : null}
          {healthQuery.isError ? (
            <ErrorState
              compact
              title="Não conseguimos consultar a saúde agora"
              description={humanizeError(healthQuery.error)}
              onRetry={() => void healthQuery.refetch()}
            />
          ) : null}
        </div>
      )}
    </Drawer>
  );
}

/* ========================================================================== */
/*  DEGRADED, explicado                                                        */
/* ========================================================================== */

interface DegradedExplanationProps {
  report: ReturnType<typeof useMonitorHealth>['data'];
}

function DegradedExplanation({ report }: DegradedExplanationProps) {
  const janela =
    report !== undefined ? formatDuration(report.silenceThresholdMs) : 'mais de meia hora';
  const silencio = report?.silentForMs ?? null;

  return (
    <section className="flex flex-col gap-2 rounded-md border border-warning/40 bg-surface px-3 py-2.5">
      <p className="text-sm font-medium text-warning">O que significa &ldquo;Instável&rdquo;</p>
      <p className="text-sm text-muted-foreground">
        O número continua conectado, mas não recebemos nenhuma movimentação de grupo há {janela}
        {silencio !== null ? ` (o último sinal foi há ${formatDuration(silencio)})` : ''}. Na
        prática isso quase sempre quer dizer que o aviso automático que o WhatsApp manda para o
        MGDLead parou de chegar — e, enquanto durar, nenhuma entrada ou saída está sendo contada.
      </p>
      <p className="text-sm text-muted-foreground">
        O primeiro passo é tocar em <strong className="font-medium">Sincronizar estado</strong>. Se
        voltar a ficar instável logo depois, é caso de rever a configuração de avisos do número, não
        de reconectar o WhatsApp.
      </p>
    </section>
  );
}

/* ========================================================================== */
/*  Peças                                                                      */
/* ========================================================================== */

interface FactProps {
  label: string;
  value: ReactNode;
  /** Carimbo exato, para o `title` do valor relativo. */
  title?: string | null;
  /** Texto de "isso nunca aconteceu" — diferente de zero (invariante 8). */
  empty?: string;
}

function Fact({ label, value, title, empty }: FactProps) {
  const vazio = value === null || value === undefined || value === '';

  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <dt className="shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right text-sm">
        {vazio ? (
          <span className="text-muted-foreground">{empty ?? NO_DATA_DASH}</span>
        ) : (
          <span {...(title ? { title } : {})}>{value}</span>
        )}
      </dd>
    </div>
  );
}

function relative(value: Date | string | null): string | null {
  return formatRelative(value);
}

/** Palavra solta do estado, para caber numa frase de toast. */
function statusWord(status: MonitorStatus): string {
  switch (status) {
    case 'CONNECTED':
      return 'conectado';
    case 'CONNECTING':
      return 'conectando';
    case 'DISCONNECTED':
      return 'desconectado';
    case 'ERROR':
      return 'com erro';
    case 'QR_REQUIRED':
      return 'aguardando a leitura do QR';
    case 'DEGRADED':
      return 'instável';
  }
}

/**
 * A faixa dos últimos 30 dias — pedido do dono (19/09), da tela do concorrente.
 *
 * Responde o que nenhum número da gaveta responde: *"este número caiu quantas vezes no
 * mês?"*. "Conectado agora" e "de pé o mês inteiro" são coisas diferentes para quem decide
 * pôr verba em cima daquele número.
 *
 * O dia sem medição fica cinza, e não verde: é o nosso silêncio, não a saúde do cliente.
 */
function Atividade({
  workspaceId,
  monitorId,
  aberto,
}: {
  workspaceId: string;
  monitorId: string;
  aberto: boolean;
}) {
  const atividade = useMonitorActivity(workspaceId, monitorId, aberto);
  const dias = atividade.data?.dias ?? [];
  /** `?.` também no `uptime`: resposta de versão anterior do servidor não traz o campo. */
  const uptime = atividade.data?.uptime ?? undefined;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-foreground">Atividade (30 dias)</h3>

      {atividade.isPending ? (
        <Skeleton className="h-4 w-full" />
      ) : dias.length === 0 || uptime === undefined ? (
        <p className="text-xs text-muted-foreground">
          A faixa começa a ser preenchida na próxima varredura — ela olha a cada 5 minutos.
        </p>
      ) : (
        /* D6: o "% conectado" vem pronto do servidor, ponderado por medição. */
        <UptimeStrip
          label="Conectividade do monitorador"
          fraction={uptime.fracao}
          daysWithoutRecord={uptime.diasSemRegistro}
          dias={dias.map((d) => ({
            dia: d.dia,
            estado: d.estado,
            ...(d.estado === 'sem-medicao'
              ? {}
              : {
                  detalhe:
                    d.falhas > 0
                      ? `${d.falhas} de ${d.medicoes} verificações com problema${d.ultimaCausa ? ` (${d.ultimaCausa})` : ''}`
                      : `${d.medicoes} verificações, todas no ar`,
                }),
          }))}
        />
      )}
    </section>
  );
}
